// ============================================================================
// devOverdue — the ONE overdue rule for developments (AUD-06, gate J).
//
// Every surface that says "overdue" asks this file: the header bell + its
// Deadlines panel (hooks/useDeadlineNotifications), the board card border
// (DevCardNew), the Developments grid (column, filter, sort, export, footer),
// useDevFilters, and shared.js isOverdue(). Before AUD-06 there were two rules:
// this one (it lived in deadlineHelpers.isDeadlineOverdue) and a dev_date /
// dev_settings.overdueRules rule in shared.js isOverdue(), whose results were
// rendered nowhere. David's AUD-02 decision folds them into this one.
//
// The rule:
//   due        = data.status_set_at + STATUS_DAYS[stage][status] days
//                (the budgets are shared.js STATUS_DAYS — re-approved by David 09.10.26, DEV-03)
//   overdue    = now is past due, and the dev is not snoozed into the future
//                (data.deadline_snoozed_until), and it is not excluded
//   excluded   = deleted (column), archived, "Moved to order" lock — as before —
//                plus stop_reason, completedAt or collection_id set (AUD-02 guards),
//                plus a plain OUT (DEV-03): decided OUT for SELECTION_SEASON and the status
//                not changed since (status_set_at <= decided_at, both readable dates). A
//                status change after the decision brings the clock back; an unreadable or
//                missing decided_at never excludes. IN is not excluded by this rule.
//   stuck      = more than STUCK_AFTER_DAYS whole days past due: off the bell, into
//                the panel's "Stuck — needs review" list. Still overdue everywhere else.
//
// No budget (Done, a terminal status, an unknown status) or no usable
// status_set_at means no due date, and never overdue.
//
// Import cycle note: shared.js imports isDevOverdue from here and this file
// imports STATUS_DAYS from shared.js. Both are read only inside functions,
// never at module evaluation, so the cycle is safe in webpack and in the
// tests' Babel loader. Keep it that way — no top-level read of STATUS_DAYS.
// ============================================================================

import { STATUS_DAYS } from '../shared';
import { devIsMovedToOrder } from './devLock';
import { SELECTION_SEASON } from '../components/developments/selectionSeason';

/** Whole days past due after which a dev leaves the bell for "Stuck — needs review". */
export const STUCK_AFTER_DAYS = 180;

const MS_PER_DAY = 86400000;

const isSet = (v) => v !== undefined && v !== null && v !== '';
// Dual read, the codebase's shape: a projection lifts data keys to the top
// level, a full-row load nests them under `data`.
const dual = (dev, key) => (isSet(dev?.[key]) ? dev[key] : dev?.data?.[key]);

/** Day budget for the dev's current stage + status (STATUS_DAYS), or null when there is none. */
export function devBudgetDays(dev) {
  const stage = dev?.stage_status || dev?.data?.stage_status;
  const status = dev?.full_status || dev?.data?.full_status;
  if (!stage || !status) return null;
  const days = STATUS_DAYS?.[stage]?.[status];
  return days === undefined || days === null ? null : days;
}

/** Due time in ms (status_set_at + budget), or NaN when the dev has no due date. */
export function devDueMs(dev) {
  const budget = devBudgetDays(dev);
  const setAt = dev?.data?.status_set_at || dev?.status_set_at;
  if (budget === null || !setAt) return NaN;
  const setMs = new Date(setAt).getTime();
  return Number.isFinite(setMs) ? setMs + budget * MS_PER_DAY : NaN;
}

const readMs = (v) => (isSet(v) ? new Date(v).getTime() : NaN);

/** DEV-03 (David 09.10.26): decided OUT for SELECTION_SEASON, and no status change since. One
 *  shape for both reads: the full list carries data->selections, the bell's slim read rebuilds
 *  the same { [season]: { decision, decided_at } } (hooks/useDeadlineDevs). Never guessed: a
 *  decided_at or status_set_at that is missing or unparseable answers false. */
export function devIsPlainOut(dev) {
  const sel = dual(dev, 'selections');
  const s = sel && typeof sel === 'object' && !Array.isArray(sel) ? sel[SELECTION_SEASON] : null;
  if (!s || typeof s !== 'object' || s.decision !== 'out') return false;
  const decided = readMs(s.decided_at);
  const setAt = readMs(dev?.data?.status_set_at || dev?.status_set_at);
  if (!Number.isFinite(decided) || !Number.isFinite(setAt)) return false;
  return setAt <= decided;
}

/** True when the dev can never be overdue: deleted, archived, locked, stopped, completed, in a collection, or a plain OUT. */
export function devOverdueExcluded(dev) {
  if (!dev) return true;
  if (dev.deleted_at) return true;                      // the column; both dev reads also drop it server-side
  if (dev.archived || dev.data?.archived) return true;
  if (devIsMovedToOrder(dev)) return true;              // Collection C2: the clock is frozen while locked
  if (isSet(dual(dev, 'stop_reason'))) return true;     // AUD-02 guard
  if (isSet(dual(dev, 'completedAt'))) return true;     // AUD-02 guard
  if (isSet(dual(dev, 'collection_id'))) return true;   // AUD-02 guard
  if (devIsPlainOut(dev)) return true;                  // DEV-03: a plain OUT stops ringing
  return false;
}

// Every public predicate takes the dev alone and reads the clock itself, so
// `rows.filter(isDevOverdue)` can never hand it an array index as "now".
function snoozedAt(dev, now) {
  const raw = dev?.data?.deadline_snoozed_until || dev?.deadline_snoozed_until;
  if (!raw) return false;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) && ms > now;
}

function stateAt(dev, now) {
  if (devOverdueExcluded(dev)) return 'none';
  const due = devDueMs(dev);
  if (!Number.isFinite(due) || now <= due) return 'none';
  if (snoozedAt(dev, now)) return 'none';
  return Math.floor((now - due) / MS_PER_DAY) > STUCK_AFTER_DAYS ? 'stuck' : 'overdue';
}

function daysAt(dev, now) {
  if (stateAt(dev, now) === 'none') return 0;
  return Math.max(0, Math.floor((now - devDueMs(dev)) / MS_PER_DAY));
}

/** True while "Postpone" has pushed the reminder into the future. */
export function devIsSnoozed(dev) {
  return snoozedAt(dev, Date.now());
}

/** 'none' | 'overdue' | 'stuck' — the whole rule in one answer. */
export function devOverdueState(dev) {
  return stateAt(dev, Date.now());
}

/** Past due at any age (stuck included) — the grid, the card border and isOverdue() read this. */
export function isDevOverdue(dev) {
  return stateAt(dev, Date.now()) !== 'none';
}

/** More than STUCK_AFTER_DAYS whole days past due. */
export function isDevStuck(dev) {
  return stateAt(dev, Date.now()) === 'stuck';
}

/** Whole days past due (>= 0); 0 when the dev is not overdue. */
export function devDaysPastDue(dev) {
  return daysAt(dev, Date.now());
}

/**
 * The bell's split, on one reading of the clock. Each item is { dev, daysOver },
 * most overdue first. `overdue` rings the bell; `stuck` goes to "Stuck — needs review".
 */
export function splitOverdue(devs) {
  const now = Date.now();
  const overdue = [];
  const stuck = [];
  for (const dev of devs || []) {
    const state = stateAt(dev, now);
    if (state === 'none') continue;
    (state === 'stuck' ? stuck : overdue).push({ dev, daysOver: daysAt(dev, now) });
  }
  const byDays = (a, b) => b.daysOver - a.daysOver;
  overdue.sort(byDays);
  stuck.sort(byDays);
  return { overdue, stuck };
}
